/**
 * The operator readings, as a state a screen can render.
 *
 * `unavailable` is its own state rather than an error. A deployment composed
 * without the reader answers 503, and that is a fact about the deployment, not
 * a fault to apologise for — but it is also not an all-clear, and the screen
 * has to be able to tell those apart.
 */
import { useQuery } from "@tanstack/react-query";

import { readSystem } from "@/shared/api";
import type { SystemResponse } from "@/shared/api";
import { statusOf } from "@/shared/http/errors";

export type SystemState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unavailable" }
  | { readonly status: "unreadable" }
  | { readonly status: "ready"; readonly system: SystemResponse };

export function useSystem(): SystemState {
  const query = useQuery({
    queryKey: ["system"],
    queryFn: async (): Promise<SystemResponse> => {
      const { data, error, response } = await readSystem();
      if (error !== undefined || data === undefined) {
        throw Object.assign(new Error("system unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
    // Faster than the account figures. This screen exists to shorten the gap
    // between something stopping and somebody knowing, and a minute of that
    // gap is a minute of slots standing idle.
    refetchInterval: 10_000,
    retry: false,
  });

  // Offline refresh cannot confirm that a cached reconciliation is current.
  if (query.fetchStatus === "paused") return { status: "unreadable" };
  if (query.isPending) return { status: "loading" };
  if (query.isError) {
    const status = statusOf(query.error);
    if (status === 403) return { status: "forbidden" };
    if (status === 503) return { status: "unavailable" };
    return { status: "unreadable" };
  }
  return { status: "ready", system: query.data };
}
